import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import SafeLifeLogo from "../components/SafeLifeLogo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { insuranceTypes, findInsuranceBySlug } from "../data/insurances";
import { toast } from "sonner";

const STATUS_LABELS = {
  novo: { label: "Novo", color: "bg-blue-100 text-blue-700" },
  em_atendimento: { label: "Em atendimento", color: "bg-amber-100 text-amber-800" },
  fechado: { label: "Fechado", color: "bg-green-100 text-green-700" },
  perdido: { label: "Perdido", color: "bg-gray-100 text-gray-700" },
};

export default function AdminDashboard() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus !== "all") params.status = filterStatus;
      if (filterType !== "all") params.insurance_type = filterType;
      if (search.trim()) params.search = search.trim();

      const [qres, sres] = await Promise.all([
        api.get("/admin/quotes", { params }),
        api.get("/admin/quotes/stats"),
      ]);
      setQuotes(qres.data.items || []);
      setStats(sres.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterType, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const updateQuote = async (id, updates) => {
    try {
      const { data } = await api.patch(`/admin/quotes/${id}`, updates);
      setQuotes((qs) => qs.map((q) => (q.id === id ? data : q)));
      if (selected?.id === id) setSelected(data);
      toast.success("Cotação atualizada");
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteQuote = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir esta cotação?")) return;
    try {
      await api.delete(`/admin/quotes/${id}`);
      setQuotes((qs) => qs.filter((q) => q.id !== id));
      setSelected(null);
      toast.success("Cotação excluída");
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="min-h-screen bg-surface" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="bg-white border-b border-navy-100 sticky top-0 z-30">
        <div className="container mx-auto px-4 sm:px-6 max-w-7xl flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3">
            <SafeLifeLogo className="h-10" />
            <span className="hidden sm:inline text-xs uppercase tracking-widest text-navy/50 font-bold border-l border-navy-100 pl-3">
              Painel
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline text-sm text-navy/70">
              Olá, <strong>{user?.name || "Admin"}</strong>
            </span>
            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="rounded-full"
              data-testid="admin-logout-btn"
            >
              <i className="fa-solid fa-right-from-bracket mr-2" /> Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-10">
        <div className="mb-8">
          <p className="text-lego text-xs font-bold uppercase tracking-widest mb-2">Painel</p>
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-navy">
            Cotações e notificações
          </h1>
        </div>

        <Tabs defaultValue="quotes" className="space-y-6">
          <TabsList className="bg-white border border-navy-100 p-1 rounded-full h-11">
            <TabsTrigger
              value="quotes"
              className="rounded-full data-[state=active]:bg-navy data-[state=active]:text-white px-5"
              data-testid="tab-quotes"
            >
              <i className="fa-solid fa-layer-group mr-2" /> Cotações
            </TabsTrigger>
            <TabsTrigger
              value="notifications"
              className="rounded-full data-[state=active]:bg-navy data-[state=active]:text-white px-5"
              data-testid="tab-notifications"
            >
              <i className="fa-brands fa-whatsapp mr-2" /> Notificações
            </TabsTrigger>
            <TabsTrigger
              value="templates"
              className="rounded-full data-[state=active]:bg-navy data-[state=active]:text-white px-5"
              data-testid="tab-templates"
            >
              <i className="fa-solid fa-file-lines mr-2" /> Templates
            </TabsTrigger>
          </TabsList>

          <TabsContent value="quotes" className="space-y-6">

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <StatCard label="Total" value={stats.total} color="bg-navy text-white" icon="fa-layer-group" testId="stat-total" />
            <StatCard label="Novos" value={stats.by_status.novo} color="bg-white text-navy border border-navy-100" icon="fa-star" testId="stat-novo" />
            <StatCard label="Em atendimento" value={stats.by_status.em_atendimento} color="bg-white text-navy border border-navy-100" icon="fa-user-clock" testId="stat-em-atendimento" />
            <StatCard label="Fechadas" value={stats.by_status.fechado} color="bg-white text-navy border border-navy-100" icon="fa-circle-check" testId="stat-fechado" />
            <StatCard label="Perdidas" value={stats.by_status.perdido} color="bg-white text-navy border border-navy-100" icon="fa-circle-xmark" testId="stat-perdido" />
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-3xl p-5 border border-navy-100 mb-6">
          <div className="grid md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Buscar</Label>
              <Input
                placeholder="Nome, e-mail ou WhatsApp..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="admin-search"
              />
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger data-testid="admin-filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="novo">Novo</SelectItem>
                  <SelectItem value="em_atendimento">Em atendimento</SelectItem>
                  <SelectItem value="fechado">Fechado</SelectItem>
                  <SelectItem value="perdido">Perdido</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Tipo de seguro</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger data-testid="admin-filter-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {insuranceTypes.map((i) => (
                    <SelectItem key={i.slug} value={i.slug}>{i.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={fetchData} variant="outline" className="w-full rounded-full" data-testid="admin-refresh-btn">
                <i className="fa-solid fa-arrows-rotate mr-2" /> Atualizar
              </Button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-3xl border border-navy-100 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-navy/60">
              <i className="fa-solid fa-spinner fa-spin text-2xl" />
              <p className="mt-2">Carregando...</p>
            </div>
          ) : quotes.length === 0 ? (
            <div className="p-12 text-center text-navy/60" data-testid="admin-empty-state">
              <i className="fa-solid fa-inbox text-3xl text-navy/30 mb-3" />
              <p>Nenhuma cotação encontrada.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-navy-50">
                  <tr className="text-left text-navy/70 text-xs uppercase tracking-wider">
                    <th className="p-4">Cliente</th>
                    <th className="p-4">Seguro</th>
                    <th className="p-4">Contato</th>
                    <th className="p-4">Cidade</th>
                    <th className="p-4">Status</th>
                    <th className="p-4">Criado em</th>
                    <th className="p-4 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {quotes.map((q) => {
                    const ins = findInsuranceBySlug(q.insurance_type);
                    const st = STATUS_LABELS[q.status] || STATUS_LABELS.novo;
                    return (
                      <tr
                        key={q.id}
                        className="border-t border-navy-100 hover:bg-surface cursor-pointer"
                        onClick={() => setSelected(q)}
                        data-testid={`quote-row-${q.id}`}
                      >
                        <td className="p-4">
                          <p className="font-semibold text-navy">{q.full_name}</p>
                          <p className="text-xs text-navy/50">{q.email}</p>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            {ins && <i className={`fa-solid ${ins.icon} text-lego`} />}
                            <span className="text-navy">{ins?.short || q.insurance_type}</span>
                          </div>
                        </td>
                        <td className="p-4 text-navy/70">{q.whatsapp}</td>
                        <td className="p-4 text-navy/70">{q.city}/{q.state}</td>
                        <td className="p-4">
                          <Badge className={`${st.color} hover:${st.color} font-semibold`}>{st.label}</Badge>
                        </td>
                        <td className="p-4 text-xs text-navy/50">
                          {new Date(q.created_at).toLocaleString("pt-BR")}
                        </td>
                        <td className="p-4 text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); setSelected(q); }}
                            data-testid={`quote-view-${q.id}`}
                          >
                            <i className="fa-solid fa-eye" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
          </TabsContent>

          <TabsContent value="notifications">
            <NotificationsPanel />
          </TabsContent>

          <TabsContent value="templates">
            <TemplatesPanel />
          </TabsContent>
        </Tabs>
      </main>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quote-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl text-navy">
                  {selected.full_name}
                </DialogTitle>
              </DialogHeader>
              <QuoteDetail
                quote={selected}
                onUpdate={(u) => updateQuote(selected.id, u)}
                onDelete={() => deleteQuote(selected.id)}
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, color, icon, testId }) {
  return (
    <div className={`rounded-2xl p-5 ${color}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs uppercase tracking-wider font-semibold opacity-70">{label}</p>
        <i className={`fa-solid ${icon} opacity-60`} />
      </div>
      <p className="font-heading text-3xl font-extrabold">{value}</p>
    </div>
  );
}

function QuoteDetail({ quote, onUpdate, onDelete }) {
  const ins = findInsuranceBySlug(quote.insurance_type);
  const [adminNotes, setAdminNotes] = useState(quote.admin_notes || "");

  return (
    <div className="space-y-5">
      {/* Status */}
      <div className="flex flex-wrap items-center gap-3 pb-4 border-b border-navy-100">
        <Label className="text-xs">Status</Label>
        <Select
          value={quote.status}
          onValueChange={(v) => onUpdate({ status: v })}
        >
          <SelectTrigger className="w-52" data-testid="detail-status-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="novo">Novo</SelectItem>
            <SelectItem value="em_atendimento">Em atendimento</SelectItem>
            <SelectItem value="fechado">Fechado</SelectItem>
            <SelectItem value="perdido">Perdido</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-4 text-sm">
        <Info label="Seguro" value={ins?.name || quote.insurance_type} icon={ins?.icon} />
        <Info label="E-mail" value={quote.email} />
        <Info label="WhatsApp" value={quote.whatsapp} />
        <Info label="Cidade/UF" value={`${quote.city}/${quote.state}`} />
        <Info label="Criado em" value={new Date(quote.created_at).toLocaleString("pt-BR")} />
        <Info label="Atualizado em" value={new Date(quote.updated_at).toLocaleString("pt-BR")} />
      </div>

      {/* Details */}
      <div>
        <p className="text-xs uppercase tracking-widest text-navy/50 font-bold mb-3">
          Dados específicos
        </p>
        <div className="bg-surface rounded-xl p-4 space-y-2 text-sm">
          {Object.entries(quote.details || {}).length === 0 && (
            <p className="text-navy/50 text-sm italic">Nenhum detalhe específico.</p>
          )}
          {Object.entries(quote.details || {}).map(([k, v]) => {
            const field = ins?.quoteFields?.find((f) => f.name === k);
            return (
              <div key={k} className="flex justify-between gap-3">
                <span className="text-navy/60">{field?.label || k}</span>
                <span className="text-navy font-semibold text-right">{String(v)}</span>
              </div>
            );
          })}
        </div>
      </div>

      {quote.notes && (
        <div>
          <p className="text-xs uppercase tracking-widest text-navy/50 font-bold mb-2">
            Observações do cliente
          </p>
          <div className="bg-surface rounded-xl p-4 text-sm text-navy/80">
            {quote.notes}
          </div>
        </div>
      )}

      <div>
        <Label className="text-xs">Notas internas (admin)</Label>
        <Textarea
          rows={3}
          value={adminNotes}
          onChange={(e) => setAdminNotes(e.target.value)}
          placeholder="Registre o que foi conversado, acompanhamentos, propostas..."
          data-testid="detail-admin-notes"
        />
        <Button
          onClick={() => onUpdate({ admin_notes: adminNotes })}
          size="sm"
          className="mt-2 bg-navy hover:bg-navy-900 text-white rounded-full"
          data-testid="detail-save-notes-btn"
        >
          Salvar notas
        </Button>
      </div>

      <DialogFooter className="pt-4 border-t border-navy-100">
        <Button
          variant="outline"
          onClick={onDelete}
          className="text-lego border-lego/40 hover:bg-lego hover:text-white rounded-full"
          data-testid="detail-delete-btn"
        >
          <i className="fa-solid fa-trash mr-2" /> Excluir
        </Button>
      </DialogFooter>
    </div>
  );
}

function Info({ label, value, icon }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-navy/50 font-semibold">{label}</p>
      <p className="text-navy mt-1 font-medium">
        {icon && <i className={`fa-solid ${icon} text-lego mr-2`} />}
        {value}
      </p>
    </div>
  );
}

function NotificationsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testMessage, setTestMessage] = useState("🧪 Teste do painel Safe Life.");
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/notifications", { params: { limit: 50 } });
      setItems(data.items || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const sendTest = async () => {
    setTesting(true);
    try {
      const body = { message: testMessage };
      if (testTo.trim()) body.to = testTo.trim();
      const { data } = await api.post("/admin/notifications/test", body);
      if (data.sent) {
        toast.success("Notificação de teste enviada!");
      } else {
        toast.error(`Falha ao enviar: ${data.reason || "erro desconhecido"}`);
      }
      fetchItems();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTesting(false);
    }
  };

  const sent = items.filter((i) => i.ok).length;
  const failed = items.length - sent;

  return (
    <div className="space-y-6" data-testid="notifications-panel">
      {/* Stats + test */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="bg-[#25D366] text-white rounded-2xl p-5" data-testid="notif-stat-sent">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-wider font-semibold opacity-80">Enviadas</p>
            <i className="fa-brands fa-whatsapp text-2xl opacity-80" />
          </div>
          <p className="font-heading text-3xl font-extrabold">{sent}</p>
        </div>
        <div className="bg-white text-navy border border-navy-100 rounded-2xl p-5" data-testid="notif-stat-failed">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-wider font-semibold opacity-70">Falhas</p>
            <i className="fa-solid fa-triangle-exclamation opacity-60" />
          </div>
          <p className="font-heading text-3xl font-extrabold">{failed}</p>
        </div>
        <div className="bg-navy text-white rounded-2xl p-5 relative overflow-hidden">
          <div className="absolute inset-0 brick-pattern opacity-30" />
          <div className="relative">
            <p className="text-xs uppercase tracking-wider font-semibold opacity-80 mb-2">Integração</p>
            <p className="font-heading font-bold">SafeFind API</p>
            <p className="text-xs opacity-70 mt-1">Canal WhatsApp conectado via Baileys</p>
          </div>
        </div>
      </div>

      {/* Test sender */}
      <div className="bg-white rounded-3xl p-6 border border-navy-100" data-testid="notif-test-sender">
        <h3 className="font-heading text-lg font-bold text-navy mb-1">Enviar notificação de teste</h3>
        <p className="text-sm text-navy/60 mb-4">
          Use para verificar se o canal do WhatsApp está conectado. Deixe o campo "Para" vazio para enviar ao número padrão configurado.
        </p>
        <div className="grid md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <Label className="text-xs">Mensagem</Label>
            <Input
              value={testMessage}
              onChange={(e) => setTestMessage(e.target.value)}
              data-testid="notif-test-message"
            />
          </div>
          <div>
            <Label className="text-xs">Para (opcional)</Label>
            <Input
              placeholder="5511999999999"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              data-testid="notif-test-to"
            />
          </div>
        </div>
        <Button
          onClick={sendTest}
          disabled={testing || !testMessage.trim()}
          className="mt-4 bg-lego hover:bg-lego/90 text-white rounded-full h-11 px-6 font-semibold"
          data-testid="notif-test-send-btn"
        >
          {testing ? (
            <><i className="fa-solid fa-spinner fa-spin mr-2" /> Enviando...</>
          ) : (
            <><i className="fa-brands fa-whatsapp mr-2" /> Enviar teste</>
          )}
        </Button>
      </div>

      {/* History */}
      <div className="bg-white rounded-3xl border border-navy-100 overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-navy-100">
          <h3 className="font-heading text-lg font-bold text-navy">Histórico (últimas 50)</h3>
          <Button size="sm" variant="outline" onClick={fetchItems} className="rounded-full" data-testid="notif-refresh-btn">
            <i className="fa-solid fa-arrows-rotate mr-2" /> Atualizar
          </Button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-navy/60">
            <i className="fa-solid fa-spinner fa-spin text-2xl" />
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-navy/60" data-testid="notif-empty-state">
            <i className="fa-brands fa-whatsapp text-3xl text-navy/30 mb-3" />
            <p>Nenhuma notificação enviada ainda.</p>
            <p className="text-xs mt-1">Notificações são enviadas automaticamente quando uma cotação ou mensagem é recebida.</p>
          </div>
        ) : (
          <div className="divide-y divide-navy-100">
            {items.map((n) => (
              <div key={n.id} className="p-5 hover:bg-surface transition-colors" data-testid={`notif-item-${n.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge
                        className={`${
                          n.ok
                            ? "bg-green-100 text-green-700 hover:bg-green-100"
                            : "bg-red-100 text-red-700 hover:bg-red-100"
                        } font-semibold`}
                      >
                        {n.ok ? (
                          <><i className="fa-solid fa-check mr-1" /> Entregue</>
                        ) : (
                          <><i className="fa-solid fa-xmark mr-1" /> Falhou</>
                        )}
                      </Badge>
                      <span className="text-xs text-navy/50 font-mono">HTTP {n.status_code ?? "—"}</span>
                      <span className="text-xs text-navy/50">
                        → {n.to}
                      </span>
                    </div>
                    <p className="text-sm text-navy/80 whitespace-pre-wrap break-words font-medium">
                      {n.preview}
                      {n.preview && n.preview.length >= 120 && "..."}
                    </p>
                  </div>
                  <p className="text-xs text-navy/50 shrink-0">
                    {new Date(n.created_at).toLocaleString("pt-BR")}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TemplatesPanel() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { code, body, enabled, name, description }
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState("");
  const [resetting, setResetting] = useState(false);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/templates");
      setTemplates(data.items || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const openEditor = (tpl) => {
    setEditing({ ...tpl });
    setPreview("");
  };

  const closeEditor = () => {
    setEditing(null);
    setPreview("");
  };

  const doPreview = async () => {
    if (!editing?.body) return;
    try {
      const { data } = await api.post("/admin/templates/preview", { body: editing.body });
      setPreview(data.rendered);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveTemplate = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/templates/${editing.code}`, {
        body: editing.body,
        enabled: editing.enabled,
        name: editing.name,
        description: editing.description,
      });
      setTemplates((ts) => ts.map((t) => (t.code === editing.code ? data : t)));
      toast.success("Template salvo");
      closeEditor();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (tpl, enabled) => {
    try {
      const { data } = await api.patch(`/admin/templates/${tpl.code}`, { enabled });
      setTemplates((ts) => ts.map((t) => (t.code === tpl.code ? data : t)));
      toast.success(enabled ? "Template ativado" : "Template desativado");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const resetToDefault = async () => {
    if (!editing) return;
    if (!window.confirm("Restaurar o conteúdo padrão deste template? A edição atual será perdida.")) return;
    setResetting(true);
    try {
      const { data } = await api.post(`/admin/templates/${editing.code}/reset`);
      setEditing({ ...data });
      setTemplates((ts) => ts.map((t) => (t.code === data.code ? data : t)));
      setPreview("");
      toast.success("Template restaurado ao padrão");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="templates-panel">
      <div className="bg-navy text-white rounded-3xl p-6 relative overflow-hidden">
        <div className="absolute inset-0 brick-pattern opacity-30" />
        <div className="relative flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
          <div className="w-14 h-14 bg-lego/20 rounded-2xl flex items-center justify-center shrink-0">
            <i className="fa-solid fa-file-lines text-lego text-2xl" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading text-xl font-bold">Templates de mensagens</h3>
            <p className="text-sm text-white/70 mt-1">
              Personalize as mensagens enviadas ao consultor e aos clientes. Use variáveis como{" "}
              <code className="bg-white/10 px-1.5 py-0.5 rounded text-lego">{"{{name}}"}</code>,{" "}
              <code className="bg-white/10 px-1.5 py-0.5 rounded text-lego">{"{{insurance}}"}</code>,{" "}
              <code className="bg-white/10 px-1.5 py-0.5 rounded text-lego">{"{{protocol}}"}</code>{" "}
              etc. Blocos condicionais:{" "}
              <code className="bg-white/10 px-1.5 py-0.5 rounded text-lego">{"{{#notes}}...{{/notes}}"}</code>.
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center text-navy/60">
          <i className="fa-solid fa-spinner fa-spin text-2xl" />
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {templates.map((tpl) => (
            <div
              key={tpl.code}
              className="bg-white rounded-2xl p-6 border border-navy-100 flex flex-col"
              data-testid={`template-card-${tpl.code}`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <Badge
                    className={`${
                      tpl.audience === "admin"
                        ? "bg-navy text-white hover:bg-navy"
                        : "bg-lego/10 text-lego hover:bg-lego/10"
                    } font-bold mb-2`}
                  >
                    {tpl.audience === "admin" ? "Para consultor" : "Para cliente"}
                  </Badge>
                  <h4 className="font-heading font-bold text-navy leading-tight">{tpl.name}</h4>
                  <p className="text-xs text-navy/60 mt-1">{tpl.description}</p>
                </div>
                <Switch
                  checked={!!tpl.enabled}
                  onCheckedChange={(v) => toggleEnabled(tpl, v)}
                  data-testid={`template-switch-${tpl.code}`}
                />
              </div>
              <pre className="bg-surface border border-navy-100 rounded-xl p-3 text-xs text-navy/80 font-mono flex-1 whitespace-pre-wrap break-words overflow-hidden max-h-32">
                {tpl.body}
              </pre>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-navy-100">
                <span className="text-[10px] uppercase tracking-wider text-navy/40 font-mono">
                  {tpl.code}
                </span>
                <Button
                  size="sm"
                  onClick={() => openEditor(tpl)}
                  className="bg-navy hover:bg-navy-900 text-white rounded-full"
                  data-testid={`template-edit-${tpl.code}`}
                >
                  <i className="fa-solid fa-pen mr-2" /> Editar
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && closeEditor()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="template-editor-dialog">
          {editing && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl text-navy">
                  Editar template
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                <div className="flex items-center justify-between bg-surface rounded-xl p-3">
                  <div>
                    <p className="font-heading font-bold text-navy text-sm">{editing.name}</p>
                    <p className="text-xs text-navy/50 font-mono">{editing.code}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-navy/70 font-semibold">Ativo</span>
                    <Switch
                      checked={!!editing.enabled}
                      onCheckedChange={(v) => setEditing({ ...editing, enabled: v })}
                      data-testid="editor-enabled-switch"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-xs">Nome (exibido no painel)</Label>
                  <Input
                    value={editing.name || ""}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    data-testid="editor-name"
                  />
                </div>
                <div>
                  <Label className="text-xs">Descrição</Label>
                  <Input
                    value={editing.description || ""}
                    onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                    data-testid="editor-description"
                  />
                </div>

                <div>
                  <Label className="text-xs">Corpo da mensagem</Label>
                  <Textarea
                    rows={10}
                    value={editing.body || ""}
                    onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                    className="font-mono text-sm"
                    data-testid="editor-body"
                  />
                  {editing.variables?.length ? (
                    <div className="mt-2 flex flex-wrap gap-2">
                      <span className="text-xs text-navy/60 font-semibold">Variáveis:</span>
                      {editing.variables.map((v) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => {
                            setEditing((ed) => ({ ...ed, body: (ed.body || "") + `{{${v}}}` }));
                          }}
                          className="text-xs bg-navy-50 hover:bg-lego hover:text-white text-navy font-mono px-2 py-0.5 rounded-full transition-colors"
                          data-testid={`editor-var-${v}`}
                        >
                          {"{{" + v + "}}"}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>

                <div className="flex gap-2">
                  <Button
                    type="button"
                    onClick={doPreview}
                    variant="outline"
                    className="rounded-full"
                    data-testid="editor-preview-btn"
                  >
                    <i className="fa-solid fa-eye mr-2" /> Pré-visualizar
                  </Button>
                  <Button
                    type="button"
                    onClick={resetToDefault}
                    variant="outline"
                    disabled={resetting}
                    className="rounded-full text-navy/60"
                    data-testid="editor-reset-btn"
                  >
                    <i className="fa-solid fa-rotate-left mr-2" /> Restaurar padrão
                  </Button>
                </div>

                {preview && (
                  <div className="bg-[#e6ffe0] border border-[#25D366]/30 rounded-xl p-4" data-testid="editor-preview-output">
                    <p className="text-xs uppercase tracking-widest text-[#1a8a47] font-bold mb-2">
                      <i className="fa-brands fa-whatsapp mr-1" /> Pré-visualização
                    </p>
                    <pre className="text-sm text-navy whitespace-pre-wrap break-words font-sans">{preview}</pre>
                  </div>
                )}
              </div>

              <DialogFooter className="pt-4 border-t border-navy-100 mt-4">
                <Button variant="ghost" onClick={closeEditor} data-testid="editor-cancel-btn">
                  Cancelar
                </Button>
                <Button
                  onClick={saveTemplate}
                  disabled={saving}
                  className="bg-lego hover:bg-lego/90 text-white rounded-full px-6"
                  data-testid="editor-save-btn"
                >
                  {saving ? (
                    <><i className="fa-solid fa-spinner fa-spin mr-2" /> Salvando...</>
                  ) : (
                    <><i className="fa-solid fa-check mr-2" /> Salvar template</>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

